import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Leaf, ImageOff, ExternalLink, ArrowLeft } from 'lucide-react'
import { supabase } from '../lib/supabase'

function ShareProduct() {
  const { id } = useParams()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [imgError, setImgError] = useState(false)

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('tobacco_products')
          .select('*')
          .eq('id', id)
          .single()

        if (fetchError) throw fetchError
        if (!data) throw new Error('产品不存在')

        setProduct(data)
        updateMetaTags(data)
      } catch (err) {
        console.error('获取产品失败:', err)
        setError(err.message || '获取产品信息失败')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchProduct()
    }
  }, [id])

  const updateMetaTags = (productData) => {
    const title = `${productData.name} - 烟草信息查询`
    const description = `${productData.name}，${productData.product_type || ''}，焦油${productData.tar_content || '-'}mg，小盒价格${productData.small_box_price || '-'}。查看更多烟草产品信息。`
    const imageUrl = getLocalImagePath(productData.image_local_path)
    const fullImageUrl = imageUrl ? `${window.location.origin}${imageUrl}` : `${window.location.origin}/vite.svg`
    const pageUrl = window.location.href

    document.title = title

    updateMetaTag('og:title', title)
    updateMetaTag('og:description', description)
    updateMetaTag('og:image', fullImageUrl)
    updateMetaTag('og:url', pageUrl)
    updateMetaTag('og:type', 'product')
    updateMetaTag('og:site_name', '烟草信息查询系统')
    updateMetaTag('og:locale', 'zh_CN')

    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', title)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:image', fullImageUrl)

    updateMetaTag('description', description)
  }

  const updateMetaTag = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"], meta[name="${property}"]`)
    if (meta) {
      meta.setAttribute('content', content)
    } else {
      meta = document.createElement('meta')
      if (property.startsWith('og:') || property.startsWith('twitter:')) {
        meta.setAttribute('property', property)
      } else {
        meta.setAttribute('name', property)
      }
      meta.setAttribute('content', content)
      document.head.appendChild(meta)
    }
  }

  const getLocalImagePath = (imageLocalPath) => {
    if (!imageLocalPath) return null
    const filename = imageLocalPath.split(/[/\\]/).pop()
    return `/images/${filename}`
  }

  const InfoItem = ({ label, value, color = 'green' }) => {
    const colorClasses = {
      green: 'from-green-50 to-emerald-50 border-green-200/50',
      amber: 'from-amber-50 to-yellow-50 border-amber-200/50',
      sky: 'from-sky-50 to-blue-50 border-sky-200/50',
    }

    return (
      <div className={`p-3.5 bg-gradient-to-br ${colorClasses[color]} border rounded-2xl`}>
        <p className="text-xs text-gray-500 mb-1 font-medium">{label}</p>
        <p className="font-semibold text-gray-800">{value || '-'}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-300/40 animate-pulse">
            <div className="w-8 h-8 border-t-2 border-white rounded-full animate-spin"></div>
          </div>
          <p className="text-green-600 font-medium">加载中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center bg-white/90 backdrop-blur-sm border border-red-200/60 rounded-3xl p-8 max-w-md">
          <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Leaf className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white font-semibold rounded-full px-6 py-3 shadow-lg shadow-green-300/40 transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            <ArrowLeft className="w-4 h-4" />
            返回首页
          </Link>
        </div>
      </div>
    )
  }

  const localImagePath = getLocalImagePath(product.image_local_path)

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-br from-white via-green-50/30 to-amber-50/20 backdrop-blur-xl border border-green-200/60 rounded-3xl shadow-2xl shadow-green-200/30 overflow-hidden">
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold text-lg">烟草信息查询</h1>
                <p className="text-white/80 text-sm">产品详情分享</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row gap-6 mb-8">
              <div className="w-40 h-40 mx-auto sm:mx-0 flex-shrink-0 bg-gradient-to-br from-green-100 to-amber-100 rounded-3xl overflow-hidden flex items-center justify-center shadow-lg shadow-green-200/30">
                {localImagePath && !imgError ? (
                  <img
                    src={localImagePath}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="flex flex-col items-center text-green-300">
                    <ImageOff className="w-12 h-12" />
                  </div>
                )}
              </div>

              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl font-bold text-green-800 mb-4 leading-tight">{product.name}</h2>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-medium border border-green-200/50">
                    {product.product_type || '未知类型'}
                  </span>
                  {product.cigarette_type && (
                    <span className="px-4 py-1.5 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 rounded-full text-sm font-medium border border-amber-200/50">
                      {product.cigarette_type}
                    </span>
                  )}
                  {product.cigarette_feature && (
                    <span className="px-4 py-1.5 bg-gradient-to-r from-sky-100 to-blue-100 text-sky-700 rounded-full text-sm font-medium border border-sky-200/50">
                      {product.cigarette_feature}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              <InfoItem label="焦油含量" value={product.tar_content ? `${product.tar_content} mg` : null} color="green" />
              <InfoItem label="烟碱含量" value={product.nicotine_content ? `${product.nicotine_content} mg` : null} color="amber" />
              <InfoItem label="一氧化碳" value={product.carbon_monoxide_content ? `${product.carbon_monoxide_content} mg` : null} color="sky" />
              <InfoItem label="小盒条码" value={product.small_box_barcode} color="green" />
              <InfoItem label="条盒条码" value={product.carton_barcode} color="green" />
              <InfoItem label="小盒价格" value={product.small_box_price} color="amber" />
              <InfoItem label="条盒价格" value={product.carton_price} color="amber" />
              <InfoItem label="批发价格" value={product.wholesale_price} color="amber" />
              <InfoItem label="上市时间" value={product.launch_time} color="sky" />
              <InfoItem label="包装形式" value={product.packaging_form} color="green" />
              <InfoItem label="烟支规格" value={product.cigarette_spec} color="sky" />
              <InfoItem label="烟支长度" value={product.cigarette_length} color="sky" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white font-semibold rounded-full px-6 py-3.5 shadow-lg shadow-green-300/40 transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                <ExternalLink className="w-5 h-5" />
                查看更多产品
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          扫描条码或搜索名称，快速查询烟草产品信息
        </p>
      </div>
    </div>
  )
}

export default ShareProduct
